<script setup>
import { useModal } from '~/composables/useModal';

const { modals, hideModal } = useModal();

const removeModal = id => {
  hideModal(id);
};

const backdropClick = modal => {
  if (modal.backdropClickClose) {
    hideModal(modal.id);
  }
};

const handleAction = (action, modalId) => {
  if (typeof action.handler === 'function') {
    action.handler(action, modalId);
  } else {
    // Default behavior is to close the modal
    hideModal(modalId);
  }
};
</script>

<template>
  <Teleport to="body">
    <transition
      v-for="modal in modals"
      :key="modal.id"
      :name="modal.transitionName || 'modal-fade'"
    >
      <div
        v-if="modal.visible"
        class="modal-backdrop-custom"
        :class="{ 'backdrop-blur': modal.blurBackdrop }"
        @click="backdropClick(modal)"
      >
        <div
          class="modal-custom"
          :class="[
            `modal-${modal.size}`,
            { 'modal-no-footer': !modal.actions || !modal.actions.length },
          ]"
          :style="{ zIndex: modal.zIndex }"
          @click.stop
        >
          <div class="modal-content-custom">
            <div class="modal-header-custom">
              <div class="d-flex align-items-center">
                <div
                  v-if="modal.icon"
                  class="modal-icon-container me-2"
                  :class="`icon-${modal.iconVariant}`"
                >
                  <i :class="modal.icon" />
                </div>
                <h5 class="modal-title-custom">{{ modal.title }}</h5>
              </div>
              <button
                v-if="modal.showCloseButton"
                type="button"
                class="btn-close-custom"
                :aria-label="$t('common.close')"
                @click="removeModal(modal.id)"
              >
                <i class="fas fa-times" />
              </button>
            </div>
            <div class="modal-body-custom">
              <div class="d-flex align-items-start flex-column flex-md-row">
                <div
                  v-if="modal.spinner"
                  class="spinner-container me-3 mb-3 mb-md-0"
                >
                  <div
                    class="spinner-custom"
                    :class="`spinner-${modal.spinnerVariant}`"
                    role="status"
                  >
                    <span class="visually-hidden">{{
                      $t('common.loading')
                    }}</span>
                  </div>
                </div>
                <div class="flex-grow-1 w-100">
                  <div v-if="modal.message" class="modal-message">
                    {{ modal.message }}
                  </div>
                  <!-- Render custom content component if provided -->
                  <component
                    :is="modal.content"
                    v-if="modal.content"
                    v-bind="modal.contentProps"
                    class="w-100 mt-2"
                  />
                </div>
              </div>
            </div>
            <!-- Render action buttons if provided -->
            <div
              v-if="modal.actions && modal.actions.length"
              class="modal-footer-custom"
            >
              <button
                v-for="(action, index) in modal.actions"
                :key="index"
                :class="[
                  'btn',
                  `btn-${action.variant || 'primary'}`,
                  { 'btn-loading': action.loading },
                ]"
                :disabled="action.loading || action.disabled"
                @click="handleAction(action, modal.id)"
              >
                <span
                  v-if="action.loading"
                  class="spinner-border spinner-border-sm me-1"
                  role="status"
                />
                <i v-if="action.icon" :class="[action.icon, 'me-1']" />
                {{ action.label }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  transition:
    backdrop-filter 0.3s ease,
    background-color 0.3s ease;
}

.backdrop-blur {
  backdrop-filter: blur(4px);
  background-color: rgba(0, 0, 0, 0.35);
}

.modal-custom {
  background: white;
  border-radius: 12px;
  box-shadow:
    0 10px 25px rgba(0, 0, 0, 0.08),
    0 2px 5px rgba(0, 0, 0, 0.05),
    0 0 0 1px rgba(0, 0, 0, 0.03);
  max-width: 90%;
  max-height: 85vh;
  overflow: auto;
  transform: translateY(0);
  border: 1px solid var(--input-border-color, #e7eaf0);
  animation: modal-slide-down 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  transition: all 0.3s ease;
  will-change: transform, opacity;
}

.modal-sm {
  width: 340px;
}

.modal-md {
  width: 520px;
}

.modal-lg {
  width: 800px;
}

.modal-xl {
  width: 1140px;
}

.modal-content-custom {
  padding: 1.5rem;
}

.modal-header-custom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--input-border-color, #e7eaf0);
  margin-bottom: 1.25rem;
}

.modal-title-custom {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-normal, #252c35);
  line-height: 1.4;
  font-family: 'Inter', sans-serif;
}

.modal-body-custom {
  padding: 0;
  font-size: 0.95rem;
}

.btn-close-custom {
  background: transparent;
  border: none;
  color: #7e8299;
  font-size: 0.9rem;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
  opacity: 0.7;
}

.btn-close-custom:hover {
  background-color: #f8f9fa;
  color: #252c35;
  opacity: 1;
}

.spinner-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
}

.spinner-custom {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  border: 2.5px solid currentColor;
  border-right-color: transparent;
  animation: spinner-border 0.85s linear infinite;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.05);
}

.spinner-primary {
  color: var(--color-primary, #189ee7);
  box-shadow: 0 0 0 4px rgba(24, 158, 231, 0.1);
}

.spinner-secondary {
  color: #6c757d;
  box-shadow: 0 0 0 4px rgba(108, 117, 125, 0.1);
}

.spinner-success {
  color: #28a745;
  box-shadow: 0 0 0 4px rgba(40, 167, 69, 0.1);
}

.spinner-danger {
  color: #dc3545;
  box-shadow: 0 0 0 4px rgba(220, 53, 69, 0.1);
}

.spinner-warning {
  color: #ffc107;
  box-shadow: 0 0 0 4px rgba(255, 193, 7, 0.1);
}

.spinner-info {
  color: #17a2b8;
  box-shadow: 0 0 0 4px rgba(23, 162, 184, 0.1);
}

.modal-icon-container {
  font-size: 1.25rem;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-right: 0.75rem;
  flex-shrink: 0;
  background-color: rgba(0, 0, 0, 0.03);
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

:global(.dark-mode) .modal-icon-container {
  background-color: rgba(255, 255, 255, 0.05);
}

.icon-primary {
  color: var(--color-primary, #189ee7);
}

.icon-success {
  color: #28a745;
}

.icon-danger {
  color: #dc3545;
}

.icon-warning {
  color: #ffc107;
}

.icon-info {
  color: #17a2b8;
}

.modal-message {
  color: #646774;
  line-height: 1.5;
  font-size: 0.95rem;
  font-weight: 400;
}

.modal-footer-custom {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 1.25rem;
  margin-top: 1.25rem;
  border-top: 1px solid var(--input-border-color, #e7eaf0);
}

.modal-no-footer .modal-body-custom {
  padding-bottom: 0.5rem;
}

/* Button enhancements */
.btn-primary {
  background-color: var(--color-primary, #189ee7);
  border-color: var(--color-primary, #189ee7);
  box-shadow: 0 2px 4px rgba(24, 158, 231, 0.15);
}

.btn-primary:hover {
  background-color: var(--color-primary-dark, #0f89cc);
  border-color: var(--color-primary-dark, #0f89cc);
  box-shadow: 0 3px 6px rgba(24, 158, 231, 0.25);
}

.btn-success {
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.15);
}

.btn-success:hover {
  box-shadow: 0 3px 6px rgba(40, 167, 69, 0.25);
}

.btn-danger {
  box-shadow: 0 2px 4px rgba(220, 53, 69, 0.15);
}

.btn-danger:hover {
  box-shadow: 0 3px 6px rgba(220, 53, 69, 0.25);
}

.btn-warning {
  box-shadow: 0 2px 4px rgba(255, 193, 7, 0.15);
}

.btn-warning:hover {
  box-shadow: 0 3px 6px rgba(255, 193, 7, 0.25);
}

.btn-info {
  box-shadow: 0 2px 4px rgba(23, 162, 184, 0.15);
}

.btn-info:hover {
  box-shadow: 0 3px 6px rgba(23, 162, 184, 0.25);
}

.btn-loading {
  opacity: 0.8;
}

/* Animation effects */
.modal-fade-enter-active {
  animation: modal-fade-in 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.modal-fade-leave-active {
  animation: modal-fade-out 0.25s cubic-bezier(0.55, 0.085, 0.68, 0.53) forwards;
}

/* Slide animations for company switching */
.modal-slide-enter-active {
  animation: modal-slide-in 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.modal-slide-leave-active {
  animation: modal-slide-out 0.3s cubic-bezier(0.55, 0.085, 0.68, 0.53) forwards;
}

@keyframes modal-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes modal-fade-out {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes modal-slide-down {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes modal-slide-in {
  from {
    transform: translateY(-30px) scale(0.95);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

@keyframes modal-slide-out {
  from {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
  to {
    transform: translateY(30px) scale(0.95);
    opacity: 0;
  }
}

@keyframes spinner-border {
  to {
    transform: rotate(360deg);
  }
}

/* Dark mode support */
:global(.dark-mode) .modal-custom {
  background: #2d3748;
  border-color: #4a5568;
  box-shadow:
    0 10px 25px rgba(0, 0, 0, 0.25),
    0 2px 5px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(0, 0, 0, 0.1);
}

:global(.dark-mode) .modal-title-custom {
  color: #e2e8f0;
}

:global(.dark-mode) .modal-message {
  color: #cbd5e0;
}

:global(.dark-mode) .modal-header-custom {
  border-bottom-color: #4a5568;
}

:global(.dark-mode) .modal-footer-custom {
  border-top-color: #4a5568;
}

:global(.dark-mode) .btn-close-custom {
  color: #a0aec0;
}

:global(.dark-mode) .btn-close-custom:hover {
  background-color: #4a5568;
  color: #e2e8f0;
}

/* Responsive adjustments */
@media (max-width: 576px) {
  .modal-sm,
  .modal-md {
    width: 95%;
    max-width: 340px;
  }

  .modal-content-custom {
    padding: 1.25rem;
  }

  .modal-title-custom {
    font-size: 1.1rem;
  }
}
</style>
